/* eslint-disable react/require-default-props */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Langs, LanguagesList } from '../../i18n';

interface LanguageSelectorProps {
  className?: string;
  onLanguageChange?: (languageCode: string) => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  onLanguageChange,
}: LanguageSelectorProps) => {
  const { i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const initialLanguage = i18n.language;
  const [selectedLanguage, setSelectedLanguage] = useState<string>(initialLanguage);

  // Initialize language from localStorage when component mounts
  useEffect(() => {
    const savedLanguage = initialLanguage;
    setSelectedLanguage(savedLanguage);
    // Ensure i18n is using the saved language (normalize to lowercase for i18n)
    const i18nCode = savedLanguage.toLowerCase();
    if (i18n && i18n.language !== i18nCode) {
      i18n.changeLanguage(i18nCode);
    }
  }, [i18n]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleChangeCulture = (languageCode: Langs) => {
    localStorage.setItem('@auth0_culture', languageCode);
  }

  const handleLanguageSelect = (languageCode: Langs) => {
    setSelectedLanguage(languageCode);
    setIsOpen(false);
    
    if (onLanguageChange) {
      onLanguageChange(languageCode);
    }

    const formattedLanguage = languageCode.toLowerCase();
    
    // Use lowercase version for i18n
    i18n.changeLanguage(formattedLanguage);
    handleChangeCulture(formattedLanguage as Langs);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`language-selector ${className}`} ref={dropdownRef} id='cadulogin-language-selector-button' onClick={toggleDropdown} aria-label="Select language">
      <div
        className="language-selector__toggle"
      >
        <span className="language-selector__current">{LanguagesList.find(lang => lang.code === selectedLanguage)?.display.toLocaleUpperCase()}</span>
      </div>
      
      {isOpen && (
        <div className="language-selector__dropdown">
          {LanguagesList.map((language) => (
            <button
              key={language.code}
              type="button"
              className={`language-selector__option ${
                selectedLanguage === language.code ? 'language-selector__option--selected' : ''
              }`}
              onClick={() => handleLanguageSelect(language.code)}
            >
              {language.name}
              {selectedLanguage === language.code && (
                <span className="language-selector__check">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
